import { createFile, MP4BoxBuffer, type Sample, type Track } from 'mp4box';
export interface VideoFramePosition { time: number; duration: number }
export function positionsFromSamples(samples: Pick<Sample, 'cts' | 'duration'>[], track: Pick<Track, 'timescale' | 'movie_timescale' | 'edits'>): VideoFramePosition[] {
  const edits = track.edits || [], active = edits.filter(edit => edit.media_time >= 0);
  if (active.length > 1 || edits.some(edit => edit.media_rate_integer !== 1 || edit.media_rate_fraction !== 0)) throw new Error('Сложный монтаж внутри контейнера: точный индекс кадров недоступен.');
  const leading = edits[0]?.media_time === -1 ? edits[0].segment_duration / track.movie_timescale : 0;
  const offset = (active[0]?.media_time || 0) / track.timescale;
  const end = active[0] ? leading + active[0].segment_duration / track.movie_timescale : Infinity;
  return samples.map(sample => ({ time: sample.cts / track.timescale - offset + leading, duration: sample.duration / track.timescale }))
    .filter(frame => frame.time + frame.duration > 0 && frame.time < end)
    .map(frame => ({ ...frame, time: Math.max(0, frame.time) })).sort((a, b) => a.time - b.time);
}
export function frameAt(frames: VideoFramePosition[], time: number) {
  let left = 0, right = frames.length - 1;
  while (left < right) { const middle = Math.ceil((left + right) / 2); if (frames[middle].time <= time + .00001) left = middle; else right = middle - 1; }
  return left;
}
export async function readVideoFrames(url: string, signal: AbortSignal, localBlob?: Blob | null) {
  const resolved = new URL(url, window.location.href);
  if (resolved.origin !== window.location.origin && resolved.protocol !== 'blob:') throw new Error('Нет доступа к индексу кадров на другом сервере.');
  const file = createFile(false); let frames: VideoFramePosition[] = [], failure = '', ready = false;
  file.onError = () => { failure = 'Не удалось прочитать индекс кадров этого видео.'; };
  file.onReady = info => {
    ready = true;
    if (info.isFragmented) { failure = 'Для этого потокового видео доступен только приблизительный шаг.'; return; }
    const track = info.videoTracks[0]; if (!track) { failure = 'Видеодорожка не найдена.'; return; }
    try { frames = positionsFromSamples(file.getTrackSamplesInfo(track.id), track); } catch (error) { failure = error instanceof Error ? error.message : 'Нет индекса кадров.'; }
  };
  let offset = 0;
  try {
    for (let part = 0; part < 64 && !ready && !failure; part++) {
      signal.throwIfAborted();
      if (localBlob) {
        const buffer = await localBlob.slice(offset, offset + 262144).arrayBuffer();
        if (!buffer.byteLength) break;
        const next = file.appendBuffer(MP4BoxBuffer.fromArrayBuffer(buffer, offset));
        offset = next > offset ? next : offset + buffer.byteLength;
        continue;
      }
      const response = await fetch(resolved, { headers: { Range: `bytes=${offset}-${offset + 262143}` }, signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Не удалось прочитать индекс кадров.');
      if (response.status === 200 && Number(response.headers.get('content-length')) > 16000000) { await response.body?.cancel(); throw new Error('Сервер не поддерживает быстрый доступ к кадрам большого файла.'); }
      const reader = response.body?.getReader(); if (!reader) throw new Error('Пустое видео.');
      const chunks: Uint8Array[] = []; let length = 0;
      while (true) { const { done, value } = await reader.read(); if (done) break; length += value.length;
        if (length > 16000000) { await reader.cancel(); throw new Error('Индекс кадров слишком большой.'); } chunks.push(value);
      }
      const buffer = new Uint8Array(length); let cursor = 0; chunks.forEach(chunk => { buffer.set(chunk, cursor); cursor += chunk.length; });
      const next = file.appendBuffer(MP4BoxBuffer.fromArrayBuffer(buffer.buffer, response.status === 200 ? 0 : offset));
      if (ready || failure || response.status === 200 || !length) break;
      offset = next > offset ? next : offset + length;
    }
    if (!frames.length) throw new Error(failure || 'Для этого формата доступен приблизительный шаг по времени.');
    return frames;
  } finally { file.stop(); }
}
